import React, { useCallback, useEffect, useRef, useState } from "react";
import { OnboardingLayout } from "../components/OnboardingFlow/OnboardingLayout.tsx";
import { WelcomeStep } from "../components/OnboardingFlow/WelcomeStep.tsx";
import { AccountSetupStep } from "../components/OnboardingFlow/AccountSetupStep.tsx";
import { PreferencesStep } from "../components/OnboardingFlow/PreferencesStep.tsx";
import { ExperienceStep } from "../components/OnboardingFlow/experienceStep.tsx";
import { CompletionStep } from "../components/OnboardingFlow/CompletionStep.tsx";
import { auth, db, ensureRemoteConfig, remoteConfig } from "../services/firebase";
import { doc, getDoc } from "firebase/firestore";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  FULL_FLOW_STEPS,
  OnboardingStepId,
  resolveFlowSteps,
} from "../components/OnboardingFlow/onboardingFlowConfig.ts";
import {
  getExperimentName,
  getExperimentVariantLabel,
  isFastPathEnabledForUser,
} from "../services/onboardingRemoteConfig.ts";
import {
  logExperimentExposure,
  logOnboardingStarted,
  logOnboardingStepComplete,
  logOnboardingStepView,
  logOnboardingAbandon,
} from "../analytics/onboarding.ts";

const SESSION_START_KEY = "drillflo_onboarding_session_started";

function OnboardingAccountSetup() {
  const navigate = useNavigate();
  const [user, authLoading] = useAuthState(auth);
  const [searchParams] = useSearchParams();
  const [flowSteps, setFlowSteps] = useState<OnboardingStepId[]>(FULL_FLOW_STEPS);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [canProgress, setCanProgress] = useState(true);
  const [ready, setReady] = useState(false);
  const [pathId, setPathId] = useState("full_v1");
  const [experimentName, setExperimentName] = useState("onboarding_flow_v1");
  const [experimentVariant, setExperimentVariant] = useState("full_wizard");
  const [fastPath, setFastPath] = useState(false);
  const lastStepRef = useRef<string>("welcome");
  const flowInitDone = useRef(false);

  const currentStepId = flowSteps[currentIndex] ?? "welcome";
  const totalSteps = flowSteps.length;

  useEffect(() => {
    const handler = () => {
      logOnboardingAbandon({
        flow_id: pathId,
        experiment_name: experimentName,
        experiment_variant: experimentVariant,
        last_step_id: lastStepRef.current,
      });
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [pathId, experimentName, experimentVariant]);

  useEffect(() => {
    lastStepRef.current = currentStepId;
  }, [currentStepId]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login");
      return;
    }
    if (flowInitDone.current) return;
    flowInitDone.current = true;

    (async () => {
      await ensureRemoteConfig();
      const name = getExperimentName(remoteConfig);
      setExperimentName(name);

      const flowParam = searchParams.get("flow");
      let useFast = false;
      if (flowParam === "fast") useFast = true;
      else if (flowParam === "full") useFast = false;
      else useFast = isFastPathEnabledForUser(remoteConfig, user.uid);

      const resolved = resolveFlowSteps({ fastPath: useFast });
      setFastPath(useFast);
      setPathId(resolved.pathId);
      setFlowSteps(resolved.steps);
      const variantLabel = getExperimentVariantLabel(useFast);
      setExperimentVariant(variantLabel);

      logExperimentExposure({
        experiment_name: name,
        experiment_variant: variantLabel,
        flow_id: resolved.pathId,
      });

      let msSinceRegistration: number | undefined;
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        const created = snap.data()?.created?.toDate?.() as Date | undefined;
        if (created) {
          msSinceRegistration = Date.now() - created.getTime();
        }
      } catch {
        // non-blocking
      }

      if (!sessionStorage.getItem(SESSION_START_KEY)) {
        sessionStorage.setItem(SESSION_START_KEY, String(Date.now()));
        logOnboardingStarted({
          flow_id: resolved.pathId,
          experiment_name: name,
          experiment_variant: variantLabel,
          ms_since_registration: msSinceRegistration,
        });
      }

      setReady(true);
    })();
  }, [authLoading, user, searchParams, navigate]);

  useEffect(() => {
    if (!ready) return;
    logOnboardingStepView({
      flow_id: pathId,
      step_id: currentStepId,
      experiment_name: experimentName,
      experiment_variant: experimentVariant,
    });
  }, [ready, currentIndex, currentStepId, pathId, experimentName, experimentVariant]);

  const handleValidChange = useCallback((isValid: boolean) => {
    setCanProgress(isValid);
  }, []);

  const handleNext = () => {
    logOnboardingStepComplete({
      flow_id: pathId,
      step_id: currentStepId,
      experiment_name: experimentName,
      experiment_variant: experimentVariant,
    });
    if (currentIndex < totalSteps - 1) {
      setCurrentIndex((i) => i + 1);
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex((i) => i - 1);
    }
  };

  const renderStep = () => {
    switch (currentStepId) {
      case "welcome":
        return <WelcomeStep />;
      case "role":
        return (
          <AccountSetupStep
            onValidChange={handleValidChange}
            experimentVariant={experimentVariant}
          />
        );
      case "preferences":
        return <PreferencesStep onValidChange={handleValidChange} />;
      case "experience":
        return <ExperienceStep onValidChange={handleValidChange} />;
      case "completion":
        return <CompletionStep />;
      default:
        return null;
    }
  };

  useEffect(() => {
    if (currentStepId === "welcome" || currentStepId === "completion") {
      setCanProgress(true);
    }
  }, [currentStepId]);

  if (authLoading || !user || !ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-500 to-violet-600 text-white">
        Loading…
      </div>
    );
  }

  return (
    <OnboardingLayout
      currentStep={currentIndex + 1}
      totalSteps={totalSteps}
      onNext={handleNext}
      onBack={handleBack}
      canProgress={canProgress}
      flowId={pathId}
      experimentName={experimentName}
      experimentVariant={experimentVariant}
      fastPath={fastPath}
    >
      {renderStep()}
    </OnboardingLayout>
  );
}

export default OnboardingAccountSetup;
